import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  canAddMoreFilterRows,
  getAvailableSearchTypeOptions,
  getEmptyFilterValue,
  getMultiSelectValues,
  getNextAvailableSearchType,
  getSearchPlaceholder,
  hasFilterRowValue,
} from "../../helper/common/buildSearchFilters";
import { ABO_COLORS } from "../../pages/CampaignAbo/lib/campaignAboStyles";

const MAX_FILTERS = 4;
const CONTROL_HEIGHT = 32;
const CONTROL_FONT_SIZE = "0.75rem";
const CONTROL_FONT_SIZE_SMALL = "0.6875rem";
const CONTROL_BORDER_COLOR = "#c4c4c4";
const CONTROL_BORDER_RADIUS = "4px";

const andLabelSx = {
  fontSize: CONTROL_FONT_SIZE_SMALL,
  fontWeight: 600,
  color: ABO_COLORS.muted,
  letterSpacing: "0.04em",
  lineHeight: 1,
  py: 0.25,
};

const sharedSelectInnerSx = {
  minHeight: "unset !important",
  height: `${CONTROL_HEIGHT}px !important`,
  py: "0 !important",
  px: 1,
  display: "flex",
  alignItems: "center",
  boxSizing: "border-box",
  lineHeight: 1.2,
};

const getStaticOutlineSx = (overrides = {}) => ({
  borderColor: `${CONTROL_BORDER_COLOR} !important`,
  borderWidth: "1px !important",
  ...overrides,
});

const sharedOutlinedRootSx = {
  height: CONTROL_HEIGHT,
  boxSizing: "border-box",
  "&.MuiInputBase-root": {
    height: CONTROL_HEIGHT,
  },
  "& .MuiOutlinedInput-notchedOutline": getStaticOutlineSx(),
  "&:hover .MuiOutlinedInput-notchedOutline": getStaticOutlineSx(),
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": getStaticOutlineSx(),
};

const selectSx = {
  ...sharedOutlinedRootSx,
  width: "100%",
  fontSize: CONTROL_FONT_SIZE,
  fontWeight: 600,
  color: ABO_COLORS.body,
  bgcolor: ABO_COLORS.slotBg,
  borderRadius: `${CONTROL_BORDER_RADIUS} 0 0 ${CONTROL_BORDER_RADIUS}`,
  "& .MuiSelect-select": {
    ...sharedSelectInnerSx,
    fontWeight: 600,
    px: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": getStaticOutlineSx({ borderRight: "none" }),
  "&:hover .MuiOutlinedInput-notchedOutline": getStaticOutlineSx({ borderRight: "none" }),
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": getStaticOutlineSx({
    borderRight: "none",
  }),
};

const inputSx = {
  flex: 1,
  minWidth: 0,
  "& .MuiInputBase-root": {
    height: CONTROL_HEIGHT,
    minHeight: CONTROL_HEIGHT,
    fontSize: CONTROL_FONT_SIZE,
    color: ABO_COLORS.body,
    bgcolor: "#fff",
    borderRadius: 0,
    boxSizing: "border-box",
  },
  "& .MuiInputBase-input": {
    fontSize: CONTROL_FONT_SIZE,
    py: 0,
    px: 0.75,
    height: "100%",
    boxSizing: "border-box",
  },
  "& .MuiInputBase-input::placeholder": {
    fontSize: CONTROL_FONT_SIZE,
    color: ABO_COLORS.muted,
    opacity: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": getStaticOutlineSx({ borderLeft: "none" }),
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": getStaticOutlineSx({
    borderLeft: "none",
  }),
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
    getStaticOutlineSx({ borderLeft: "none" }),
};

const valueSelectSx = {
  ...sharedOutlinedRootSx,
  width: "100%",
  fontSize: CONTROL_FONT_SIZE,
  fontWeight: 500,
  color: ABO_COLORS.body,
  bgcolor: "#fff",
  borderRadius: 0,
  "& .MuiSelect-select": {
    ...sharedSelectInnerSx,
    fontWeight: 500,
    px: 0.75,
  },
  "& .MuiOutlinedInput-notchedOutline": getStaticOutlineSx({ borderLeft: "none" }),
  "&:hover .MuiOutlinedInput-notchedOutline": getStaticOutlineSx({ borderLeft: "none" }),
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": getStaticOutlineSx({
    borderLeft: "none",
  }),
};

const fieldFormControlSx = {
  minWidth: 132,
  flexShrink: 0,
  height: CONTROL_HEIGHT,
  "& .MuiOutlinedInput-root": {
    height: CONTROL_HEIGHT,
  },
};

const valueFormControlSx = {
  flex: 1,
  minWidth: 0,
  minHeight: CONTROL_HEIGHT,
  "& .MuiOutlinedInput-root": {
    minHeight: CONTROL_HEIGHT,
    height: "auto",
  },
};

const valueChipSx = {
  height: 22,
  maxWidth: "100%",
  bgcolor: ABO_COLORS.infoBg,
  color: ABO_COLORS.body,
  border: `1px solid ${ABO_COLORS.infoBorder}`,
  fontWeight: 600,
  "& .MuiChip-label": {
    px: 0.75,
    fontSize: CONTROL_FONT_SIZE_SMALL,
    lineHeight: 1.3,
  },
  "& .MuiChip-deleteIcon": {
    fontSize: 14,
    color: ABO_COLORS.muted,
    "&:hover": {
      color: ABO_COLORS.body,
    },
  },
};

function ValueChips({ values = [], getLabel, onDelete }) {
  if (!values.length) return null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        gap: 0.5,
        alignItems: "center",
        maxWidth: "100%",
      }}
    >
      {values.map((value, index) => (
        <Chip
          key={`${value}-${index}`}
          label={getLabel ? getLabel(value) : value}
          size="small"
          onDelete={onDelete ? () => onDelete(index) : undefined}
          sx={valueChipSx}
        />
      ))}
    </Box>
  );
}

function ChipTextInput({
  row,
  isFirstRow,
  placeholder,
  onValueChange,
  onClearRow,
  onKeyDown,
  valueBorderRadius,
}) {
  const [inputText, setInputText] = useState("");
  const chips = getMultiSelectValues(row.value);

  const updateChips = (nextChips) => {
    onValueChange(row.id, nextChips.join(","));
  };

  const commitTokens = (text) => {
    const tokens = getMultiSelectValues(text);
    if (!tokens.length) return;

    const merged = [...chips];
    tokens.forEach((token) => {
      if (!merged.includes(token)) {
        merged.push(token);
      }
    });
    updateChips(merged);
    setInputText("");
  };

  const handleInputChange = (event) => {
    const text = event.target.value;
    if (text.includes(",")) {
      commitTokens(text);
      return;
    }
    setInputText(text);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (inputText.trim()) {
        commitTokens(inputText);
      } else {
        onKeyDown?.(event);
      }
      return;
    }

    if (event.key === "Backspace" && !inputText && chips.length) {
      updateChips(chips.slice(0, -1));
      return;
    }

    if (event.key !== "Enter") {
      onKeyDown?.(event);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    commitTokens(event.clipboardData.getData("text"));
  };

  const removeChip = (index) => {
    updateChips(chips.filter((_, chipIndex) => chipIndex !== index));
  };

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
        minHeight: CONTROL_HEIGHT,
        px: isFirstRow ? 0.5 : 0.75,
        py: 0.5,
        bgcolor: "#fff",
        border: `1px solid ${CONTROL_BORDER_COLOR}`,
        borderLeft: "none",
        borderRight: isFirstRow ? "none" : undefined,
        borderRadius: valueBorderRadius,
        boxSizing: "border-box",
      }}
    >
      {isFirstRow ? (
        <SearchIcon
          sx={{ fontSize: 15, color: ABO_COLORS.muted, ml: 0.25, flexShrink: 0 }}
        />
      ) : null}

      <ValueChips values={chips} onDelete={removeChip} />

      <Box
        component="input"
        value={inputText}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        onPaste={handlePaste}
        placeholder={chips.length ? "Add more..." : placeholder}
        sx={{
          flex: 1,
          minWidth: 72,
          border: "none",
          outline: "none",
          bgcolor: "transparent",
          fontSize: CONTROL_FONT_SIZE,
          color: ABO_COLORS.body,
          lineHeight: 1.2,
          py: 0.25,
          px: 0.25,
          "&::placeholder": {
            color: ABO_COLORS.muted,
            opacity: 1,
          },
        }}
      />

      {chips.length > 0 || inputText ? (
        <IconButton
          size="small"
          onClick={() => {
            setInputText("");
            onClearRow(row.id);
          }}
          sx={{
            p: 0.35,
            color: ABO_COLORS.muted,
            flexShrink: 0,
            "&:hover": {
              color: ABO_COLORS.body,
              bgcolor: ABO_COLORS.slotBg,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      ) : null}
    </Box>
  );
}

export function AppliedFilterChip({ filter, searchTypeOptions, onDelete }) {
  const option = searchTypeOptions.find(
    (searchOption) => searchOption.value === filter.searchType
  );
  const filterLabel = option?.label ?? filter.searchType;
  const usesValueChips =
    option?.inputType === "multiSelect" || option?.operator === "IN";
  const values = usesValueChips
    ? getMultiSelectValues(filter.searchText)
    : [filter.searchText];

  const getValueLabel = (value) =>
    option?.choices?.find((choice) => choice.value === value)?.label ?? value;

  return (
    <Chip
      size="small"
      onDelete={onDelete}
      label={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
            py: 0.25,
          }}
        >
          <Typography
            component="span"
            sx={{ fontSize: CONTROL_FONT_SIZE, fontWeight: 700, lineHeight: 1.3 }}
          >
            {filterLabel}:
          </Typography>
          {usesValueChips ? (
            <ValueChips values={values} getLabel={getValueLabel} />
          ) : (
            <Typography
              component="span"
              sx={{ fontSize: CONTROL_FONT_SIZE, fontWeight: 500, lineHeight: 1.3 }}
            >
              {filter.searchText}
            </Typography>
          )}
        </Box>
      }
      sx={{
        height: "auto",
        alignItems: "center",
        "& .MuiChip-label": {
          display: "block",
          py: 0.5,
          whiteSpace: "normal",
        },
        "& .MuiChip-deleteIcon": {
          marginTop: 0,
          marginBottom: 0,
        },
      }}
    />
  );
}

function renderFilterValueInput({
  row,
  isFirstRow,
  selectedOption,
  placeholder,
  onValueChange,
  onClearRow,
  onKeyDown,
}) {
  const isMultiSelectInput = selectedOption?.inputType === "multiSelect";
  const isSelectInput = selectedOption?.inputType === "select";
  const isChipTextInput =
    selectedOption?.operator === "IN" && !isMultiSelectInput;
  const valueBorderRadius = isFirstRow ? 0 : `0 ${CONTROL_BORDER_RADIUS} ${CONTROL_BORDER_RADIUS} 0`;

  if (isChipTextInput) {
    return (
      <ChipTextInput
        row={row}
        isFirstRow={isFirstRow}
        placeholder={placeholder}
        onValueChange={onValueChange}
        onClearRow={onClearRow}
        onKeyDown={onKeyDown}
        valueBorderRadius={valueBorderRadius}
      />
    );
  }

  if (isMultiSelectInput) {
    const selectedValues = getMultiSelectValues(row.value);

    return (
      <FormControl size="small" sx={valueFormControlSx}>
        <Select
          multiple
          value={selectedValues}
          onChange={(event) => onValueChange(row.id, event.target.value)}
          displayEmpty
          sx={{
            ...valueSelectSx,
            borderRadius: valueBorderRadius,
            height: "auto",
            minHeight: CONTROL_HEIGHT,
            "&.MuiInputBase-root": {
              height: "auto",
              minHeight: CONTROL_HEIGHT,
            },
            "& .MuiSelect-select": {
              ...valueSelectSx["& .MuiSelect-select"],
              height: "auto !important",
              minHeight: `${CONTROL_HEIGHT}px !important`,
              py: "4px !important",
              whiteSpace: "normal",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              ...valueSelectSx["& .MuiOutlinedInput-notchedOutline"],
              borderRight: isFirstRow ? "none" : undefined,
            },
          }}
          MenuProps={{ disableScrollLock: true }}
          renderValue={(selected) => {
            if (!selected.length) {
              return (
                <Box
                  component="span"
                  sx={{
                    fontSize: CONTROL_FONT_SIZE,
                    color: ABO_COLORS.muted,
                    lineHeight: 1.2,
                  }}
                >
                  {placeholder}
                </Box>
              );
            }

            return (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  alignItems: "center",
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                <ValueChips
                  values={selected}
                  getLabel={(value) =>
                    selectedOption?.choices?.find(
                      (option) => option.value === value
                    )?.label ?? value
                  }
                />
              </Box>
            );
          }}
        >
          {selectedOption?.choices?.map((choice) => (
            <MenuItem
              key={choice.value}
              value={choice.value}
              sx={{ fontSize: CONTROL_FONT_SIZE, py: 0.25 }}
            >
              <Checkbox
                size="small"
                checked={selectedValues.indexOf(choice.value) > -1}
                sx={{ p: 0.5, mr: 0.5 }}
              />
              <ListItemText
                primary={choice.label}
                primaryTypographyProps={{ fontSize: CONTROL_FONT_SIZE }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (isSelectInput) {
    return (
      <FormControl size="small" sx={valueFormControlSx}>
        <Select
          value={row.value}
          onChange={(event) => onValueChange(row.id, event.target.value)}
          displayEmpty
          sx={{
            ...valueSelectSx,
            borderRadius: valueBorderRadius,
            "& .MuiOutlinedInput-notchedOutline": {
              ...valueSelectSx["& .MuiOutlinedInput-notchedOutline"],
              borderRight: isFirstRow ? "none" : undefined,
            },
          }}
          MenuProps={{ disableScrollLock: true }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Box
                  component="span"
                  sx={{
                    fontSize: CONTROL_FONT_SIZE,
                    color: ABO_COLORS.muted,
                    lineHeight: 1.2,
                  }}
                >
                  {placeholder}
                </Box>
              );
            }
            const choice = selectedOption?.choices?.find(
              (option) => option.value === selected
            );
            return (
              <Box component="span" sx={{ fontSize: CONTROL_FONT_SIZE, lineHeight: 1.2 }}>
                {choice?.label ?? selected}
              </Box>
            );
          }}
        >
          {selectedOption?.choices?.map((choice) => (
            <MenuItem
              key={choice.value}
              value={choice.value}
              sx={{ fontSize: CONTROL_FONT_SIZE }}
            >
              {choice.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      size="small"
      placeholder={placeholder}
      variant="outlined"
      value={row.value}
      onChange={(event) => onValueChange(row.id, event.target.value)}
      onKeyDown={onKeyDown}
      sx={{
        ...inputSx,
        "& .MuiInputBase-root": {
          ...inputSx["& .MuiInputBase-root"],
          borderRadius: valueBorderRadius,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          ...inputSx["& .MuiOutlinedInput-notchedOutline"],
          borderRight: isFirstRow ? "none" : undefined,
        },
      }}
      InputProps={{
        startAdornment: isFirstRow ? (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 15, color: ABO_COLORS.muted }} />
          </InputAdornment>
        ) : null,
        endAdornment: row.value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => onClearRow(row.id)}
              edge="end"
              sx={{
                p: 0.35,
                color: ABO_COLORS.muted,
                "&:hover": {
                  color: ABO_COLORS.body,
                  bgcolor: ABO_COLORS.slotBg,
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
}

function MultiFilterSearch({
  searchTypeOptions = [],
  defaultSearchType,
  filterRows = [],
  onFilterRowsChange,
  onApply,
  onCreateRow,
  defaultExpanded = false,
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const canAddMore = canAddMoreFilterRows(
    filterRows,
    searchTypeOptions,
    MAX_FILTERS
  );
  const remainingFilters = Math.min(
    MAX_FILTERS - filterRows.length,
    searchTypeOptions.length - filterRows.length
  );
  const filledFilterCount = filterRows.filter((row) =>
    hasFilterRowValue(row, searchTypeOptions)
  ).length;

  const collapsedSummary =
    filledFilterCount > 0
      ? `${filledFilterCount} filter${filledFilterCount > 1 ? "s" : ""} configured`
      : null;

  const handleToggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleSearchTypeChange = (rowId, nextType) => {
    onFilterRowsChange(
      filterRows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              searchType: nextType,
              value: getEmptyFilterValue(nextType, searchTypeOptions),
            }
          : row
      )
    );
  };

  const handleValueChange = (rowId, nextValue) => {
    onFilterRowsChange(
      filterRows.map((row) =>
        row.id === rowId ? { ...row, value: nextValue } : row
      )
    );
  };

  const handleClearRow = (rowId) => {
    onFilterRowsChange(
      filterRows.map((row) => {
        if (row.id !== rowId) return row;
        return {
          ...row,
          value: getEmptyFilterValue(row.searchType, searchTypeOptions),
        };
      })
    );
  };

  const handleRemoveRow = (rowId) => {
    if (filterRows.length === 1) {
      onFilterRowsChange([onCreateRow(defaultSearchType)]);
      return;
    }
    onFilterRowsChange(filterRows.filter((row) => row.id !== rowId));
  };

  const handleAddFilter = () => {
    if (!canAddMore) return;
    const nextSearchType = getNextAvailableSearchType(
      filterRows,
      searchTypeOptions,
      defaultSearchType
    );
    onFilterRowsChange([...filterRows, onCreateRow(nextSearchType)]);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onApply?.();
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        border: `1px solid ${ABO_COLORS.border}`,
        borderRadius: CONTROL_BORDER_RADIUS,
        bgcolor: ABO_COLORS.cardBg,
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={handleToggleExpanded}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          width: "100%",
          px: 1.25,
          py: 0.6,
          bgcolor: ABO_COLORS.tableHeadBg,
          borderBottom: isExpanded ? `1px solid ${ABO_COLORS.border}` : "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          "&:hover": {
            bgcolor: ABO_COLORS.infoBg,
          },
        }}
      >
        <FilterListIcon sx={{ fontSize: 16, color: ABO_COLORS.borderFocus }} />
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: ABO_COLORS.label,
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              lineHeight: 1.2,
              flexShrink: 0,
            }}
          >
            Search & Filters
          </Typography>
          {!isExpanded && collapsedSummary && (
            <Typography
              sx={{
                fontSize: CONTROL_FONT_SIZE_SMALL,
                fontWeight: 500,
                color: ABO_COLORS.muted,
                lineHeight: 1.2,
              }}
            >
              {collapsedSummary}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            handleToggleExpanded();
          }}
          aria-label={isExpanded ? "Collapse filters" : "Expand filters"}
          sx={{
            p: 0.35,
            ml: "auto",
            color: ABO_COLORS.borderFocus,
            bgcolor: "#fff",
            border: `1px solid ${ABO_COLORS.border}`,
            borderRadius: CONTROL_BORDER_RADIUS,
            "&:hover": {
              bgcolor: ABO_COLORS.infoBg,
              borderColor: ABO_COLORS.infoBorder,
            },
          }}
        >
          {isExpanded ? (
            <ExpandLessIcon sx={{ fontSize: 18 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit={false}>
        <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
        {filterRows.map((row, index) => {
          const isFirstRow = index === 0;
          const isLastRow = index === filterRows.length - 1;
          const placeholder = getSearchPlaceholder(
            row.searchType,
            searchTypeOptions
          );
          const selectedOption = searchTypeOptions.find(
            (option) => option.value === row.searchType
          );
          const availableSearchTypeOptions = getAvailableSearchTypeOptions(
            filterRows,
            searchTypeOptions,
            row.id
          );

          return (
            <Box key={row.id}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    flex: 1,
                    minWidth: 0,
                    minHeight: CONTROL_HEIGHT,
                    borderRadius: CONTROL_BORDER_RADIUS,
                    overflow: "hidden",
                    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                  }}
                >
                  <FormControl size="small" sx={fieldFormControlSx}>
                    <Select
                      value={row.searchType}
                      onChange={(event) =>
                        handleSearchTypeChange(row.id, event.target.value)
                      }
                      displayEmpty
                      sx={{
                        ...selectSx,
                        alignSelf: "center",
                      }}
                      MenuProps={{ disableScrollLock: true }}
                    >
                      {availableSearchTypeOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          sx={{ fontSize: CONTROL_FONT_SIZE }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {renderFilterValueInput({
                    row,
                    isFirstRow,
                    selectedOption,
                    placeholder,
                    onValueChange: handleValueChange,
                    onClearRow: handleClearRow,
                    onKeyDown: handleKeyDown,
                  })}

                  {isFirstRow && (
                    <Button
                      variant="contained"
                      onClick={onApply}
                      sx={{
                        flexShrink: 0,
                        alignSelf: "center",
                        minWidth: 68,
                        height: CONTROL_HEIGHT,
                        minHeight: CONTROL_HEIGHT,
                        px: 1.25,
                        borderRadius: `0 ${CONTROL_BORDER_RADIUS} ${CONTROL_BORDER_RADIUS} 0`,
                        fontSize: CONTROL_FONT_SIZE,
                        lineHeight: 1.2,
                        fontWeight: 600,
                        textTransform: "none",
                        bgcolor: ABO_COLORS.borderFocus,
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: "#4f3ad4",
                          boxShadow: "0 2px 8px rgba(97, 72, 246, 0.28)",
                        },
                      }}
                    >
                      Search
                    </Button>
                  )}
                </Box>

                {!isFirstRow && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveRow(row.id)}
                    aria-label={`Remove ${selectedOption?.label ?? "filter"}`}
                    sx={{
                      flexShrink: 0,
                      width: CONTROL_HEIGHT,
                      height: CONTROL_HEIGHT,
                      border: `1px solid ${ABO_COLORS.border}`,
                      borderRadius: CONTROL_BORDER_RADIUS,
                      bgcolor: "#fff",
                      color: ABO_COLORS.muted,
                      "&:hover": {
                        color: "#B91C1C",
                        borderColor: "#FECACA",
                        bgcolor: "#FEF2F2",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>

              {isLastRow && canAddMore && (
                <Box sx={{ mt: 0.65 }}>
                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                    onClick={handleAddFilter}
                    sx={{
                      textTransform: "none",
                      fontSize: CONTROL_FONT_SIZE,
                      fontWeight: 600,
                      color: ABO_COLORS.borderFocus,
                      px: 1,
                      py: 0.25,
                      borderRadius: "999px",
                      border: `1px dashed ${ABO_COLORS.infoBorder}`,
                      bgcolor: ABO_COLORS.infoBg,
                      "&:hover": {
                        bgcolor: "#EDE9FE",
                        borderColor: ABO_COLORS.borderFocus,
                      },
                    }}
                  >
                    Add more filter
                    {remainingFilters > 0 && (
                      <Typography
                        component="span"
                        sx={{
                          ml: 0.75,
                          fontSize: CONTROL_FONT_SIZE_SMALL,
                          fontWeight: 500,
                          color: ABO_COLORS.muted,
                        }}
                      >
                        ({remainingFilters} left)
                      </Typography>
                    )}
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
        </Box>
      </Collapse>
    </Box>
  );
}

export default MultiFilterSearch;
